import { useStyletron } from '@thanx/uikit/theme'
import {
  useGetInsightsLabV2FavoritesQuery,
  useGetInsightsLabV2RecommendedReportsQuery,
} from 'api/insightsLabV2'
import ArrowRight from 'components/Icons/ArrowRight'
import LookerLabs from 'components/Icons/LookerLabs'
import SectionHeader from 'components/Navbar/Bar/SectionHeader'
import useFlag from 'hooks/useFlag'
import useIsThanxAdmin from 'hooks/useIsThanxAdmin'
import useOpenSherlock from 'hooks/useOpenSherlock'
import {
  formatPermissionReason,
  usePermissionLabel,
  useRbacNavDisabledWithReason,
} from 'hooks/useRbacNavCheck'
import { buildTranslate } from 'locales'
import { useMemo } from 'react'
import { useAbility } from 'utilities/ability'
import { useIsMobile } from '../../../utilities/responsive'
import NavLink from '../Bar/NavLink'
import ExploreAllReportsLink from './ExploreAllReportsLink'

const t = buildTranslate('nav.customers_menu')

// Gaps above each section header. The drawer uses one value; the dropdown
// splits first/later.
const SECTION_GAP = '16px'
const FIRST_SECTION_GAP = '8px'
const MOBILE_SECTION_GAP = '10px'

// Row 4 (Recommended reports / Favorite reports) caps each column
// independently: show everything up to the cap, otherwise show only the
// first REPORT_COLUMN_VISIBLE_COUNT and collapse the rest into a single
// "+N more" link.
const REPORT_COLUMN_CAP = 4
const REPORT_COLUMN_VISIBLE_COUNT = 3

function capReportColumn<T>(items: T[]): {
  visible: T[]
  overflowCount: number
} {
  if (items.length <= REPORT_COLUMN_CAP) {
    return { visible: items, overflowCount: 0 }
  }
  return {
    visible: items.slice(0, REPORT_COLUMN_VISIBLE_COUNT),
    overflowCount: items.length - REPORT_COLUMN_VISIBLE_COUNT,
  }
}

type ReportColumnItem = {
  key: number
  text: string
  link: string
  // Both favorite names and recommended report titles are author-controlled
  // and can run long. See NavLink's `wrapText` prop.
  wrapText?: boolean
}

type ReportColumnProps = {
  isLeftNav: boolean
  onClick?: VoidFunction
  headerText: string
  iconName: string
  items: ReportColumnItem[]
  overflowText: (count: number) => string
  // Destination for the "+N more" overflow link. Each call site supplies its
  // own — must include a stable, non-localized `section` query param (e.g.
  // "recommended"/"favorites") so ReportBrowser knows which sidebar section
  // to land on. Never derive this from headerText, which is translated.
  overflowLink: string
}

// Shared shape for the Row 4 "Recommended reports" / "Favorite reports"
// columns — same header + capped list + optional overflow link, differing
// only in their data and copy. Mirrors the `renderFavoriteRow` extraction
// pattern in scenes/InsightsLab/v2/ReportBrowser.tsx.
const ReportColumn = (props: ReportColumnProps) => {
  const {
    isLeftNav,
    onClick,
    headerText,
    iconName,
    items,
    overflowText,
    overflowLink,
  } = props
  const [css] = useStyletron()

  // An empty column renders nothing at all, header included — the other
  // column still renders independently.
  if (items.length === 0) return null

  const { visible, overflowCount } = capReportColumn(items)

  return (
    <div
      className={`${css({
        minWidth: '240px',
      })}`}
    >
      <div
        className={css({
          height: isLeftNav ? MOBILE_SECTION_GAP : SECTION_GAP,
        })}
      />
      <SectionHeader
        isLeftNav={isLeftNav}
        text={headerText}
        iconName={iconName}
      />
      {visible.map(item => (
        <NavLink
          key={item.key}
          isLeftNav={isLeftNav}
          onClick={onClick}
          text={item.text}
          link={item.link}
          wrapText={item.wrapText}
        />
      ))}
      {overflowCount > 0 && (
        <NavLink
          isLeftNav={isLeftNav}
          onClick={onClick}
          text={overflowText(overflowCount)}
          link={overflowLink}
          wrapText={items[0]?.wrapText}
          // Match the sibling links above it (row height/padding via
          // wrapText, and default non-active background/hover). Without
          // disableActiveHighlight, this link's bare "/insights_lab" href
          // reads as the active route on every Insights Lab query-string
          // variant, making it look like the "Browse all reports" CTA
          // instead of a list item.
          disableActiveHighlight
        />
      )}
    </div>
  )
}

type Props = {
  isLeftNav?: boolean
  onClick?: VoidFunction
}

const CustomersContent = (props: Props) => {
  const { isLeftNav = false, onClick } = props
  const ability = useAbility()
  const [css] = useStyletron()
  const isMobile = useIsMobile()
  const insightsLabEnabled = ability.can('access', 'InsightsLab')
  // Insights Lab v2: replaces the Activity + Insights sections with
  // recommended reports + favorites, and adds the in-page multi-tab report
  // experience. 100% on in production — the `false` is an LD-outage fallback,
  // not a rollout gate.
  const insightsLabV2Enabled = useFlag('insights-lab-v2', false)
  // Only swap in the v2 experience for users who can actually access Insights Lab
  // (the CASL permission) — not on the LD flag alone. Without the permission the
  // /insights_lab route is blocked, so the v2 nav links would dead-end and the
  // RTK queries shouldn't fire.
  const insightsLabV2Visible = insightsLabEnabled && insightsLabV2Enabled
  const { data: recommendedReports = [] } =
    useGetInsightsLabV2RecommendedReportsQuery(undefined, {
      skip: !insightsLabV2Visible,
    })
  const { data: favoriteReports = [] } = useGetInsightsLabV2FavoritesQuery(
    undefined,
    { skip: !insightsLabV2Visible }
  )
  // Sort by recommendedPosition so the nav's ordering matches ReportBrowser's
  // recommended ordering rather than relying on the recommended_reports
  // endpoint to return rows pre-sorted by position. The null filter mirrors
  // ReportBrowser.tsx for parity — the /recommended_reports endpoint's
  // `.recommended` scope already excludes null positions server-side, so this
  // is defense-in-depth against that guarantee ever changing.
  const sortedRecommended = useMemo(
    () =>
      [...recommendedReports]
        .filter(report => report.recommendedPosition != null)
        .sort(
          (a, b) => (a.recommendedPosition ?? 0) - (b.recommendedPosition ?? 0)
        ),
    [recommendedReports]
  )
  // The API already returns favorites most-recently-created first (see
  // LookerReportFavorite.ordered) — passed straight into the Favorites
  // column as-is, this file never reorders it.
  const userListsEnabled = useFlag('self-serve-list-upload', false)
  const rbacEnabled = useFlag('role-based-access-control', false)
  // sherlock-ai-rollout is keyed by merchant_id in LaunchDarkly. Same flag
  // name is also enforced server-side by the nexus merchant-api endpoints
  // (POST /merchants/:id/sherlock/sso_token + /snowflake/execute).
  const sherlockAiEnabled = useFlag('sherlock-ai-rollout', false)
  const isThanxAdmin = useIsThanxAdmin()
  const segments = useRbacNavDisabledWithReason('Segments')
  const { openSherlock, isLoading: isOpeningSherlock } = useOpenSherlock()

  const reportsLabel = usePermissionLabel('Reports')
  const locationReportsLabel = usePermissionLabel('LocationReports')
  const usersLabel = usePermissionLabel('Users')

  // Flag-gated disabled states for report links.
  // RBAC consolidates legacy subjects into Reports / LocationReports.
  // When the flag is permanent, remove the ternaries and keep the RBAC branch.
  const reportDisabled = {
    dailyActivity: rbacEnabled
      ? ability.cannot('access', 'Reports')
      : ability.cannot('access', 'LocationReports'),
    revenue: ability.cannot('access', 'LocationReports'),
    purchases: rbacEnabled
      ? ability.cannot('access', 'LocationReports')
      : ability.cannot('access', 'PurchasesReport'),
    signups: rbacEnabled
      ? ability.cannot('access', 'Reports')
      : ability.cannot('manage', 'Insights'),
    suppressions: rbacEnabled
      ? ability.cannot('access', 'Reports')
      : ability.cannot('access', 'LocationReports'),
    overview: rbacEnabled
      ? ability.cannot('access', 'Reports')
      : ability.cannot('manage', 'LocationReports'),
    activation: rbacEnabled
      ? ability.cannot('access', 'Reports')
      : ability.cannot('manage', 'Insights'),
    engagement: rbacEnabled
      ? ability.cannot('access', 'Reports')
      : ability.cannot('access', 'LocationReports'),
    retention: rbacEnabled
      ? ability.cannot('access', 'Reports')
      : ability.cannot('manage', 'Insights'),
    captureRate: rbacEnabled
      ? ability.cannot('access', 'LocationReports')
      : ability.cannot('manage', 'OperationalReports'),
    averageCheck: rbacEnabled
      ? ability.cannot('access', 'LocationReports')
      : ability.cannot('access', 'AverageCheckReport'),
    frequency: rbacEnabled
      ? ability.cannot('access', 'Reports')
      : ability.cannot('access', 'LocationReports'),
  }

  const reportReason = {
    dailyActivity: formatPermissionReason(
      reportsLabel,
      reportDisabled.dailyActivity
    ),
    revenue: formatPermissionReason(
      locationReportsLabel,
      reportDisabled.revenue
    ),
    purchases: formatPermissionReason(
      locationReportsLabel,
      reportDisabled.purchases
    ),
    signups: formatPermissionReason(reportsLabel, reportDisabled.signups),
    suppressions: formatPermissionReason(
      reportsLabel,
      reportDisabled.suppressions
    ),
    overview: formatPermissionReason(reportsLabel, reportDisabled.overview),
    activation: formatPermissionReason(reportsLabel, reportDisabled.activation),
    engagement: formatPermissionReason(reportsLabel, reportDisabled.engagement),
    retention: formatPermissionReason(reportsLabel, reportDisabled.retention),
    captureRate: formatPermissionReason(
      locationReportsLabel,
      reportDisabled.captureRate
    ),
    averageCheck: formatPermissionReason(
      locationReportsLabel,
      reportDisabled.averageCheck
    ),
    frequency: formatPermissionReason(reportsLabel, reportDisabled.frequency),
  }

  // v1 keeps its header-free drawer: its Activity/Insights sub-headers are
  // desktop-only, so a header here would claim their rows.
  const showSectionHeaders = !isLeftNav || insightsLabV2Visible

  // Dropdown gaps ship with the v2 treatment only.
  const sectionGap = (isFirst: boolean): JSX.Element | null => {
    if (isLeftNav)
      return <div className={css({ height: MOBILE_SECTION_GAP })} />
    if (!insightsLabV2Visible) return null
    return (
      <div
        className={css({ height: isFirst ? FIRST_SECTION_GAP : SECTION_GAP })}
      />
    )
  }

  const usersDisabled = ability.cannot('access', 'Users')
  const usersReason = formatPermissionReason(usersLabel, usersDisabled)

  // Two columns on desktop/medium, one on mobile. Sections auto-flow into rows
  // in source order (so they never overlap); Insights Lab spans the full width.
  const style = {
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
    gap: isMobile ? '0px' : '0px 16px',
    alignItems: 'stretch',
    justifyItems: 'start',
  }

  return (
    <>
      <div className={css(style)}>
        <div
          className={`${css({
            minWidth: '240px',
          })}`}
        >
          {showSectionHeaders && (
            <>
              {sectionGap(true)}
              <SectionHeader
                isLeftNav={isLeftNav}
                text={t('sections.manage_customers.title')}
                iconName="manageCustomers"
              />
            </>
          )}
          <NavLink
            isLeftNav={isLeftNav}
            onClick={onClick}
            text={t('sections.manage_customers.all_customers')}
            link="/users"
            disabled={usersDisabled}
            disabledReason={usersReason}
          />
          {userListsEnabled && (
            <NavLink
              isLeftNav={isLeftNav}
              onClick={onClick}
              text={t('sections.manage_customers.user_lists')}
              link="/user_lists"
              disabled={ability.cannot('access', 'UserLists')}
              status="new"
            />
          )}
          <NavLink
            isLeftNav={isLeftNav}
            onClick={onClick}
            text={t('sections.manage_customers.segments')}
            link="/segments"
            disabled={segments.disabled}
            disabledReason={segments.disabledReason}
          />
          <NavLink
            isLeftNav={isLeftNav}
            onClick={onClick}
            text={t('sections.manage_customers.ai_segment_builder')}
            link="/segments/ai-builder"
            disabled={segments.disabled}
            disabledReason={segments.disabledReason}
            status="beta"
          />
          {insightsLabV2Visible && (
            <NavLink
              isLeftNav={isLeftNav}
              onClick={onClick}
              text={t('sections.activity.suppressions')}
              link="/reports/suppressions"
              disabled={reportDisabled.suppressions}
              disabledReason={reportReason.suppressions}
            />
          )}
          {insightsLabV2Visible && sherlockAiEnabled && isThanxAdmin && (
            <NavLink
              isLeftNav={isLeftNav}
              disabled={isOpeningSherlock}
              onClick={() => {
                if (isOpeningSherlock) return
                onClick?.()
                openSherlock()
              }}
              text={t('sections.insights.sherlock_ai')}
              status="beta"
            />
          )}
          {!insightsLabV2Visible && (
            <>
              {!isLeftNav && (
                <>
                  <div className="mb-m" />
                  <SectionHeader
                    text={t('sections.activity.title')}
                    iconName="chartLine"
                  />
                </>
              )}
              <NavLink
                isLeftNav={isLeftNav}
                onClick={onClick}
                link="/reports/daily_activity"
                text={t('sections.activity.daily_activity')}
                disabled={reportDisabled.dailyActivity}
                disabledReason={reportReason.dailyActivity}
              />
              <NavLink
                isLeftNav={isLeftNav}
                onClick={onClick}
                link="/reports/revenue"
                text={t('sections.activity.revenue')}
                disabled={reportDisabled.revenue}
                disabledReason={reportReason.revenue}
              />
              <NavLink
                isLeftNav={isLeftNav}
                onClick={onClick}
                link="/reports/purchases"
                text={t('sections.activity.purchases')}
                disabled={reportDisabled.purchases}
                disabledReason={reportReason.purchases}
              />
              <NavLink
                isLeftNav={isLeftNav}
                onClick={onClick}
                text={t('sections.activity.signups')}
                link="/reports/customer"
                disabled={reportDisabled.signups}
                disabledReason={reportReason.signups}
              />
              <NavLink
                isLeftNav={isLeftNav}
                onClick={onClick}
                text={t('sections.activity.suppressions')}
                link="/reports/suppressions"
                disabled={reportDisabled.suppressions}
                disabledReason={reportReason.suppressions}
              />
            </>
          )}
        </div>
        <div
          className={`${css({
            minWidth: '240px',
          })}`}
        >
          {showSectionHeaders && (
            <>
              {sectionGap(true)}
              <SectionHeader
                isLeftNav={isLeftNav}
                text={t('sections.customer_lifecycle.title')}
                iconName="cycle"
              />
            </>
          )}
          <NavLink
            isLeftNav={isLeftNav}
            onClick={onClick}
            text={t('sections.customer_lifecycle.overview')}
            link="/reports/membership_overview"
            disabled={reportDisabled.overview}
            disabledReason={reportReason.overview}
          />
          <NavLink
            isLeftNav={isLeftNav}
            onClick={onClick}
            text={t('sections.customer_lifecycle.activation')}
            link="/reports/activation#activation_rate"
            disabled={reportDisabled.activation}
            disabledReason={reportReason.activation}
          />
          <NavLink
            isLeftNav={isLeftNav}
            onClick={onClick}
            text={t('sections.customer_lifecycle.engagement')}
            link="/reports/engagement"
            disabled={reportDisabled.engagement}
            disabledReason={reportReason.engagement}
          />
          <NavLink
            isLeftNav={isLeftNav}
            onClick={onClick}
            text={t('sections.customer_lifecycle.retention')}
            link="/reports/retention_rate"
            disabled={reportDisabled.retention}
            disabledReason={reportReason.retention}
          />
          {!insightsLabV2Visible && (
            <>
              {!isLeftNav && (
                <>
                  <div className="mb-m" />
                  <SectionHeader
                    text={t('sections.insights.title')}
                    iconName="bulb"
                  />
                </>
              )}
              <NavLink
                isLeftNav={isLeftNav}
                onClick={onClick}
                link="/reports/capture_rate"
                text={t('sections.insights.capture_rate')}
                disabled={reportDisabled.captureRate}
                disabledReason={reportReason.captureRate}
              />
              <NavLink
                isLeftNav={isLeftNav}
                onClick={onClick}
                link="/reports/average_check"
                text={t('sections.insights.average_check')}
                disabled={reportDisabled.averageCheck}
                disabledReason={reportReason.averageCheck}
              />
              <NavLink
                isLeftNav={isLeftNav}
                onClick={onClick}
                link="/reports/frequency"
                text={t('sections.insights.frequency')}
                disabled={reportDisabled.frequency}
                disabledReason={reportReason.frequency}
              />
              {sherlockAiEnabled && isThanxAdmin && (
                <NavLink
                  isLeftNav={isLeftNav}
                  disabled={isOpeningSherlock}
                  onClick={() => {
                    if (isOpeningSherlock) return
                    onClick?.()
                    openSherlock()
                  }}
                  text={t('sections.insights.sherlock_ai')}
                  status="beta"
                />
              )}
            </>
          )}
          {!isLeftNav && <div className="mt-m" />}
        </div>
        {insightsLabV2Visible && (
          <div
            className={`${isLeftNav ? 'mt-xs' : ''} ${css({
              gridColumn: '1 / -1',
              width: '100%',
              padding: '0px',
            })}`}
          >
            {sectionGap(false)}
            <SectionHeader
              isLeftNav={isLeftNav}
              text={t('sections.insights_lab_v2.revenue.title')}
              iconName="revenue"
            />
            <NavLink
              isLeftNav={isLeftNav}
              onClick={onClick}
              text={t('sections.insights_lab_v2.revenue.capture_rate')}
              link="/reports/capture_rate"
              disabled={reportDisabled.captureRate}
              disabledReason={reportReason.captureRate}
            />
            <NavLink
              isLeftNav={isLeftNav}
              onClick={onClick}
              text={t('sections.insights_lab_v2.revenue.location_revenue')}
              link="/reports/revenue"
              disabled={reportDisabled.revenue}
              disabledReason={reportReason.revenue}
            />
          </div>
        )}
        {insightsLabV2Visible && (
          <ReportColumn
            isLeftNav={isLeftNav}
            onClick={onClick}
            headerText={t('sections.insights_lab_v2.recommended_reports')}
            iconName="chartLine"
            overflowLink="/insights_lab?section=recommended"
            items={sortedRecommended.map(report => ({
              key: report.id,
              text: report.title,
              link: `/insights_lab?open=${report.id}`,
              // Recommended report titles, like favorite names, are
              // author-controlled and can run long — wrap instead of
              // clipping/overflowing the nav column.
              wrapText: true,
            }))}
            overflowText={count =>
              t('sections.insights_lab_v2.more_reports', { count })
            }
          />
        )}
        {insightsLabV2Visible && (
          <ReportColumn
            isLeftNav={isLeftNav}
            onClick={onClick}
            headerText={t('sections.insights_lab_v2.favorites')}
            iconName="star"
            overflowLink="/insights_lab?section=favorites"
            items={favoriteReports.map(favorite => ({
              key: favorite.id,
              text: favorite.name,
              link: `/insights_lab?favorite=${favorite.id}`,
              // Favorite names, like recommended report titles, are
              // user-authored and can run long — wrap instead of
              // clipping/overflowing the nav column.
              wrapText: true,
            }))}
            overflowText={count =>
              t('sections.insights_lab_v2.more_reports', { count })
            }
          />
        )}
        {insightsLabV2Visible && (
          <div
            className={`${isLeftNav ? 'mt-xs' : ''} ${css({
              gridColumn: '1 / -1',
              width: '100%',
              padding: isLeftNav ? '14px 12px 24px' : '12px',
            })}`}
          >
            <ExploreAllReportsLink onClick={onClick} isLeftNav={isLeftNav} />
          </div>
        )}
        {insightsLabEnabled && !insightsLabV2Visible && (
          <div
            className={`${isLeftNav ? 'mt-xs' : ''} ${css({
              gridColumn: '1 / -1',
              width: '100%',
              padding: isLeftNav ? '0px 16px' : '0px',
            })}`}
          >
            <NavLink
              colorName="grey10"
              isLeftNav={isLeftNav}
              onClick={onClick}
              text={t('sections.insights_lab.navbar_link')}
              Icon={LookerLabs}
              link="/insights_lab"
              disabled={!insightsLabEnabled}
              RightIcon={ArrowRight}
            />
          </div>
        )}
      </div>
    </>
  )
}

export default CustomersContent
